<template>
  <div class="supplier-aging">
    <ReportChoices label="As on" icon="ti-calendar-time" :choices="asOfChoices" :active="activeAsOf"
                   @select="filters.date = $event.date" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 2">
        <div class="rp-filter">
          <label>As on date</label>
          <input v-model="filters.date" type="date" class="form-control" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search supplier name or phone" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-red">
        <span class="rp-card-icon"><i class="ti ti-clock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Due</div>
          <div class="rp-card-value">{{ formatMoney(summary.total_due) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.supplier_count, 'supplier') }} · {{ plural(summary.invoice_count, 'unpaid bill') }}
          </div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-orange" :class="{ selected: filters.show === 'over_90' }"
              :title="filters.show === 'over_90' ? 'Show all suppliers' : 'Show suppliers with bills over 90 days'"
              @click="toggleShow('over_90')">
        <span class="rp-card-icon"><i class="ti ti-alert-triangle"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Over 90 Days</div>
          <div class="rp-card-value" :class="{ 'text-danger': Number(summary.over_90) }">{{ formatMoney(summary.over_90) }}</div>
          <div class="rp-card-sub">{{ summary.over_90_share }}% of the total due</div>
        </div>
      </button>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-hourglass"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Average Age</div>
          <div class="rp-card-value">{{ summary.avg_age === null ? '—' : plural(summary.avg_age, 'day') }}</div>
          <div class="rp-card-sub text-truncate" :title="oldestText">{{ oldestText }}</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-teal" :class="{ selected: filters.show === 'advance' }"
              :title="filters.show === 'advance' ? 'Show all suppliers' : 'Show suppliers paid in advance'"
              @click="toggleShow('advance')">
        <span class="rp-card-icon"><i class="ti ti-coin"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Advance · Net</div>
          <div class="rp-card-value">{{ formatMoney(summary.advance) }}</div>
          <div class="rp-card-sub">net payable {{ formatMoney(summary.balance) }}</div>
        </div>
      </button>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Due by age</div>
        <div class="d-flex align-items-center gap-3">
          <div v-show="Number(summary.total_due)" class="sa-doughnut"><canvas ref="ageChart"></canvas></div>
          <ul class="sa-legend">
            <li v-for="(bucket, index) in summary.buckets" :key="bucket.key">
              <i class="rp-dot" :style="{ background: AGE_COLORS[index] }"></i>
              <span class="flex-grow-1">{{ bucket.label }}</span>
              <b>{{ bucket.share }}%</b>
            </li>
          </ul>
        </div>
        <p v-if="!Number(summary.total_due)" class="text-muted small mb-0 mt-2">Nothing is owed on this date.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Age buckets</div>
        <div class="rp-bars">
          <div v-for="bucket in bucketBars" :key="bucket.key">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ bucket.label }} <span class="text-muted">· {{ plural(bucket.count, 'bill') }}</span></span>
              <span class="fw-semibold text-nowrap">{{ formatMoney(bucket.amount) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="bucket.tone" :style="{ width: `${bucket.percent}%` }"></div></div>
          </div>
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Largest dues</div>
        <ul v-if="summary.top_suppliers.length" class="rp-ranked">
          <li v-for="(supplier, index) in summary.top_suppliers" :key="supplier.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="supplier.name">{{ supplier.name }}</div>
              <small class="text-muted">oldest bill {{ plural(supplier.oldest_days, 'day') }}</small>
            </div>
            <span class="fw-semibold text-nowrap text-danger">{{ formatMoney(supplier.total_due) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">Nothing is owed on this date.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button v-if="row.invoices.length" type="button" class="rp-expand"
                          :title="expanded[row.key] ? 'Hide bills' : 'Show bills'" @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="fw-medium">{{ row.supplier }}</div>
                  <span v-if="row.supplier_phone" class="rp-sub">{{ row.supplier_phone }}</span>
                </td>
                <td v-for="(key, index) in BUCKET_KEYS" :key="key" class="text-end"
                    :class="Number(row[key]) ? BUCKET_TEXT[index] : 'text-muted'">
                  {{ Number(row[key]) ? formatMoney(row[key]) : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.total_due) }}</td>
                <td class="text-end" :class="Number(row.advance) ? 'text-success' : 'text-muted'">
                  {{ Number(row.advance) ? formatMoney(row.advance) : '—' }}
                </td>
                <td class="text-center">
                  <template v-if="row.oldest_days !== null">
                    <span class="rp-badge" :class="ageTone(row.oldest_days)">{{ plural(row.oldest_days, 'day') }}</span>
                    <span class="rp-sub">avg {{ plural(row.avg_age, 'day') }}</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title">
                      <i class="ti ti-file-invoice me-1"></i>Unpaid bills of {{ row.supplier }}
                      <span class="text-muted fw-normal">· as on {{ formatDate(filters.date) }}</span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Bill</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th class="text-end">Amount</th>
                            <th class="text-end">Returned</th>
                            <th class="text-end">Paid</th>
                            <th class="text-end">Due</th>
                            <th class="text-end">Age</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="bill in row.invoices" :key="bill.voucher_id">
                            <td class="fw-semibold">{{ bill.voucher_id }}</td>
                            <td class="text-nowrap">{{ formatDate(bill.date) }}</td>
                            <td>{{ bill.kind === 'purchase' ? 'Purchase' : 'Other bill' }}</td>
                            <td class="text-end">{{ formatMoney(bill.amount) }}</td>
                            <td class="text-end">{{ Number(bill.returned) ? `−${formatMoney(bill.returned)}` : '—' }}</td>
                            <td class="text-end">{{ Number(bill.paid) ? formatMoney(bill.paid) : '—' }}</td>
                            <td class="text-end fw-semibold text-danger">{{ formatMoney(bill.due) }}</td>
                            <td class="text-end">
                              <span class="rp-badge" :class="ageTone(bill.age_days)">{{ plural(bill.age_days, 'day') }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-hourglass"></i>
                  <div class="fw-semibold">Nothing owed to suppliers</div>
                  <small class="text-muted">Try another date, supplier or filter.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th class="text-end">Total ({{ plural(rows.length, 'supplier') }})</th>
              <th v-for="key in BUCKET_KEYS" :key="key" class="text-end">{{ formatMoney(summary[key]) }}</th>
              <th class="text-end">{{ formatMoney(summary.total_due) }}</th>
              <th class="text-end">{{ formatMoney(summary.advance) }}</th>
              <th class="text-center">{{ summary.avg_age === null ? '' : `avg ${plural(summary.avg_age, 'day')}` }}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Age = days from the bill date to the as-on date. Payments and returns clear each supplier's oldest bills first,
      so what is left is the newest part of the payable. Every store is covered.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { localToday } from '../inventory/inventoryReportMixin';
import { addDays } from '../inventory/periodReportMixin';
import salesReport from '../sales/salesReportMixin';
import purchaseReport from './purchaseReportMixin';

const BUCKET_KEYS = ['0_30', '31_60', '61_90', '91_180', 'over_180'];
const BUCKET_LABELS = ['0–30 days', '31–60 days', '61–90 days', '91–180 days', 'Over 180 days'];
const AGE_COLORS = ['#28C76F', '#2D6CDF', '#FE9F43', '#EA5455', '#9C1C1D'];
const BUCKET_TONES = ['success', 'info', 'warning', 'danger', 'danger'];
const BUCKET_TEXT = ['', '', 'text-warning', 'text-danger', 'text-danger fw-semibold'];

const EMPTY_SUMMARY = {
  ...Object.fromEntries(BUCKET_KEYS.map((k) => [k, 0])),
  total_due: 0, advance: 0, balance: 0, supplier_count: 0, invoice_count: 0, avg_age: null, over_90: 0,
  over_90_share: 0, oldest: null, top_suppliers: [], show_counts: { all: 0 },
  buckets: BUCKET_KEYS.map((key, i) => ({ key, label: BUCKET_LABELS[i], amount: 0, share: 0, count: 0 })),
};

export default {
  name: 'SupplierAgingReport',
  mixins: [inventoryReport, salesReport, purchaseReport],

  reportUrl: apiUrls.supplier_aging_report.get,
  fileName: 'supplier_aging',
  columns: [
    { key: 'supplier', label: 'Supplier' },
    ...BUCKET_KEYS.map((key, i) => ({ key, label: BUCKET_LABELS[i], class: 'text-end', numeric: true })),
    { key: 'total_due', label: 'Total Due', class: 'text-end', numeric: true },
    { key: 'advance', label: 'Advance', class: 'text-end', numeric: true },
    { key: 'oldest_days', label: 'Oldest Bill', class: 'text-center', numeric: true },
  ],

  data() {
    const today = localToday();
    return {
      today,
      filters: {
        date: today, supplier: null, show: null, warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      BUCKET_KEYS,
      BUCKET_TEXT,
      AGE_COLORS,
    };
  },

  computed: {
    asOfChoices() {
      const today = this.today;
      return [
        { key: 'today', label: 'Today', date: today },
        { key: 'month_end', label: 'End of last month', date: addDays(`${today.slice(0, 8)}01`, -1) },
        { key: '30d', label: '30 days ago', date: addDays(today, -30) },
        { key: '90d', label: '90 days ago', date: addDays(today, -90) },
        { key: 'year_end', label: 'End of last year', date: `${Number(today.slice(0, 4)) - 1}-12-31` },
      ];
    },

    activeAsOf() {
      return this.asOfChoices.find((c) => c.date === this.filters.date)?.key || null;
    },

    hasActiveFilters() {
      const { date, supplier, show, search } = this.filters;
      return date !== this.today || !!(supplier || show || search.trim());
    },

    showPills() {
      const counts = this.summary.show_counts;
      return [
        { key: 'all', label: 'All suppliers', count: counts.all },
        { key: 'over_30', label: 'Over 30 days', count: counts.over_30 || 0 },
        { key: 'over_60', label: 'Over 60 days', count: counts.over_60 || 0 },
        { key: 'over_90', label: 'Over 90 days', count: counts.over_90 || 0 },
        { key: 'advance', label: 'With advance', count: counts.advance || 0 },
      ];
    },

    bucketBars() {
      return this.withPercent(this.summary.buckets, 'amount').map((b, i) => ({ ...b, tone: BUCKET_TONES[i] }));
    },

    oldestText() {
      const oldest = this.summary.oldest;
      return oldest ? `oldest: ${oldest.supplier} · ${this.plural(oldest.age_days, 'day')}` : 'No unpaid bills';
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.date'(date) { if (date) this.onFilterChange(); },
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { date, show } = this.filters;
      return { ...(date ? { date } : {}), ...this.supplierParams(), ...(show ? { show } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { date: this.today, supplier: null, show: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawDoughnut('age', this.$refs.ageChart, this.summary.buckets
        .map((b, i) => ({ label: b.label, value: b.amount, color: AGE_COLORS[i] }))));
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    toggleShow(key) {
      this.filters.show = this.filters.show === key ? null : key;
    },

    ageTone(days) {
      if (days > 90) return 'danger';
      if (days > 30) return 'warning';
      return 'muted';
    },

    printReport() {
      const s = this.summary;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      const search = this.filters.search.trim();
      this.printColumns(
        'Supplier Aging Report',
        [`As on: ${this.formatDate(this.filters.date)}`, `Showing: ${show}`, this.supplierText(), this.storeText(),
          search && `Search: ${search}`],
        [
          ['Total Due', `${this.formatMoney(s.total_due)} · ${this.plural(s.supplier_count, 'supplier')}`],
          ['Over 90 Days', `${this.formatMoney(s.over_90)} (${s.over_90_share}%)`],
          ['Average Age', s.avg_age === null ? '—' : this.plural(s.avg_age, 'day')],
          ['Advance', this.formatMoney(s.advance)],
        ],
        [
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Phone', value: (r) => r.supplier_phone },
          ...BUCKET_KEYS.map((key, i) => ({ label: BUCKET_LABELS[i], value: (r) => this.formatMoney(r[key]), num: true })),
          { label: 'Total Due', value: (r) => this.formatMoney(r.total_due), num: true },
          { label: 'Advance', value: (r) => this.formatMoney(r.advance), num: true },
          { label: 'Oldest (days)', value: (r) => r.oldest_days ?? '', num: true },
        ],
        this.visibleRows,
        {
          Phone: 'Total',
          ...Object.fromEntries(BUCKET_KEYS.map((key, i) => [BUCKET_LABELS[i], this.formatMoney(s[key])])),
          'Total Due': this.formatMoney(s.total_due), Advance: this.formatMoney(s.advance),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.sa-doughnut {
  position: relative;
  flex: 0 0 150px;
  width: 150px;
  height: 150px;
}

.sa-legend {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8rem;
}

.sa-legend li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
